import React from "react";
import "./MyStandingsTable.css";
import TeamLogo from "./TeamLogo";
import StateMessage from "./StateMessage";
import { hexToRgba } from "../Functions/Colors";

// Column order and labels are unchanged from the previous table (PJ PG PE PP GF GC DG PTS).
const STAT_COLUMNS = [
  { key: "games_played", label: "PJ", title: "Partidos jugados" },
  { key: "wins", label: "PG", title: "Partidos ganados" },
  { key: "draws", label: "PE", title: "Partidos empatados" },
  { key: "losses", label: "PP", title: "Partidos perdidos" },
  { key: "goals_for", label: "GF", title: "Goles a favor", optional: true },
  { key: "goals_against", label: "GC", title: "Goles en contra", optional: true },
  { key: "goal_difference", label: "DG", title: "Diferencia de goles" },
];

function formatDiff(value) {
  const n = Number(value) || 0;
  return n > 0 ? `+${n}` : String(n);
}

/**
 * League standings. Rows arrive already sorted by DynamicLeaguePage;
 * this component only presents them.
 */
const MyStandingsTable = ({
  standings = [],
  leagueName,
  displayHeading = false,
  themeColor,
  loading = false,
}) => {
  const titleId = `standings-title-${leagueName || "liga"}`;

  return (
    <section
      className="table-container"
      aria-labelledby={titleId}
      style={{
        "--theme-color": themeColor || "var(--accent)",
        "--theme-soft": hexToRgba(themeColor, 0.14),
      }}
    >
      <header className="table-head">
        <h2 className="block-title" id={titleId}>
          {displayHeading ? leagueName : "Tabla"}
        </h2>
        {standings.length > 0 && (
          <span className="table-head-note">{standings.length} equipos</span>
        )}
      </header>

      {loading && standings.length === 0 ? (
        <div className="standings-skeleton" role="status" aria-label="Cargando tabla…">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i} className="skeleton" />
          ))}
        </div>
      ) : standings.length === 0 ? (
        <StateMessage title="Tabla sin equipos">
          La tabla aparecerá cuando se registren equipos en esta liga.
        </StateMessage>
      ) : (
        <div
          className="standings-scroll"
          role="region"
          aria-labelledby={titleId}
          tabIndex={0}
        >
          <table className="standings">
            <thead>
              <tr>
                <th scope="col" className="col-pos">
                  <abbr title="Posición">#</abbr>
                </th>
                <th scope="col" className="col-team">
                  Equipo
                </th>
                {STAT_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={`col-stat${c.optional ? " col-optional" : ""}`}
                  >
                    <abbr title={c.title}>{c.label}</abbr>
                  </th>
                ))}
                <th scope="col" className="col-pts">
                  <abbr title="Puntos">PTS</abbr>
                </th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, index) => (
                <tr key={`${team.team_id}-${leagueName}`}>
                  <td className="col-pos">
                    <span className="pos-badge">{index + 1}</span>
                  </td>
                  <th scope="row" className="col-team">
                    <span className="team-cell">
                      <TeamLogo
                        name={team.team_name}
                        logoUrl={team.logo_url}
                        size="sm"
                      />
                      <span className="team-cell-name" title={team.team_name}>
                        {team.team_name}
                      </span>
                    </span>
                  </th>
                  {STAT_COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={`col-stat${c.optional ? " col-optional" : ""}${
                        c.key === "goal_difference" ? " col-diff" : ""
                      }`}
                    >
                      {c.key === "goal_difference"
                        ? formatDiff(team[c.key])
                        : team[c.key] ?? 0}
                    </td>
                  ))}
                  <td className="col-pts">{team.points ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default MyStandingsTable;
